import { ICookiesPopupProps } from '../components/common/CookiesPopUp';
import { IFooterProps } from '../components/common/layout/Footer';
import { INavBarProps } from '../components/common/layout/NavBar';
import {
  IBlogPost,
  ICommonTranslations,
  IContactMethod,
  ICookiesPopup,
  ICountry,
  ICulture,
  ICurrency,
  ICustomerReviewsMeta,
  IEntitiesStatus,
  ILanguage,
  IMainFooter,
  IMainNavBar,
} from '../models/strapi.models';
import commonTranslations from '../scripts/common-translations.json';
import contactMethods from '../scripts/contact-methods.json';
import cookiesPopUp from '../scripts/cookies-pop-up.json';
import countryNames from '../scripts/country-names.json';
import currencies from '../scripts/currencies.json';
import customerReviewsMeta from '../scripts/customer-reviews-meta.json';
import entitiesStatus from '../scripts/entities-status.json';
import languages from '../scripts/languages.json';
import mainFooter from '../scripts/main-footer.json';
import mainNavBar from '../scripts/main-nav-bar.json';
import { fetchStrapi } from '../services/api.service';
import { logInfo } from '../services/log.service';
import { getStoredData } from '../services/storage.service';
import {
  flatStrapiCollection,
  flatStrapiObject,
  getTranslation,
} from './useUtils';

export const buildApiKey = process.env.NEXT_PUBLIC_STRAPI_BUILD_API_KEY || '';
export const publicApiKey = process.env.NEXT_PUBLIC_STRAPI_API_KEY || '';

// Localization
export const getAppLanguages = async () => {
  return flatStrapiCollection<any>({ data: languages }) as ILanguage[];
};

export const getDefaultLocale = (
  routerLocale: string,
  storedLocale?: string
) => {
  // If no anything else to change, use browser locale = router locale
  let defaultLocale = routerLocale;
  let defaultLocaleMessage = 'Using router locale';

  if (!!storedLocale && routerLocale !== storedLocale) {
    defaultLocale = storedLocale;
    defaultLocaleMessage = 'Using the stored locale value';
  }

  if (routerLocale !== storedLocale) {
    // URL has a explicit locale
    defaultLocale = routerLocale;
    defaultLocaleMessage =
      'There is a stored value but URL has a explicit locale. Using router locale';
  }

  logInfo([defaultLocaleMessage, `"${defaultLocale}"`], 'App → Default locale');

  return defaultLocale;
};

// Currency
export const getAppCurrencies = async () => {
  return flatStrapiCollection<any>({ data: currencies }) as ICurrency[];
};

export const getDefaultCurrency = async (
  currencies: ICurrency[] | undefined,
  apiKey: string = publicApiKey,
  canFetchCulture: boolean = true
) => {
  let defaultCurrencyIsoCode = 'USD';
  const storedCurrencyIsoCode = getStoredData<{ code: string }>(
    'user-currency'
  )?.code;

  if (storedCurrencyIsoCode) {
    defaultCurrencyIsoCode = storedCurrencyIsoCode;
  } else {
    let culture: ICulture | undefined = undefined;

    if (canFetchCulture) {
      try {
        culture = await fetchStrapi<ICulture>(
          `/users-info/culture`,
          undefined,
          apiKey
        );
      } catch (error) {
        logInfo(
          ['Error fetching culture', (error as Error).message],
          'App → Default currency'
        );
      }
    }

    const cultureCurrency = currencies?.find(
      (c) => c.isoCode === culture?.currencyCode
    );

    if (
      !!cultureCurrency &&
      !!cultureCurrency.isoCode &&
      defaultCurrencyIsoCode !== cultureCurrency.isoCode
    ) {
      defaultCurrencyIsoCode = cultureCurrency.isoCode;
    }
  }

  const defaultCurrency = currencies?.find(
    (x) => x.isoCode === defaultCurrencyIsoCode
  );

  return defaultCurrency || undefined;
};

// Layout
export const getMainNavBar = async (locale: string) => {
  const localizedMainNavBars = mainNavBar.map((x: any) => ({
    locale: x.locale,
    data: flatStrapiObject<IMainNavBar>(x),
  }));
  return (
    localizedMainNavBars.find((x: any) => x.locale === locale)?.data ||
    localizedMainNavBars[0].data
  );
};

export const getMainFooter = async (locale: string) => {
  const localizedMainFooters = mainFooter.map((x: any) => {
    const locale = x.locale;
    const footer = flatStrapiObject<IMainFooter>(x);
    footer.blogPosts =
      flatStrapiCollection<IBlogPost>(footer.blogPosts as any) || [];

    return {
      locale,
      data: footer,
    };
  });
  return (
    localizedMainFooters.find((x: any) => x.locale === locale)?.data ||
    localizedMainFooters[0].data
  );
};

export const getContactMethods = async () => {
  return flatStrapiCollection<any>({
    data: contactMethods,
  }) as IContactMethod[];
};

export const getCookiesPopup = async (locale: string) => {
  const localizedCookiesPopUps = cookiesPopUp.map((x: any) => ({
    locale: x.locale,
    data: flatStrapiObject<ICookiesPopup>(x),
  }));

  return (
    localizedCookiesPopUps.find((x: any) => x.locale === locale)?.data ||
    localizedCookiesPopUps[0].data
  );
};

export const getCookiesAcceptance = async () => {
  const acceptance = getStoredData('cookies-acceptance') as {
    accepted: boolean;
  };

  return !!acceptance ? acceptance.accepted : false;
};

export const getCommonTranslations = async () => {
  return {
    ...commonTranslations.attributes,
  } as ICommonTranslations;
};

export const getEntitiesStatus = async () => {
  return flatStrapiObject<IEntitiesStatus>({ data: entitiesStatus });
};

export const getCustomerReviewsMeta = async () => {
  return flatStrapiObject<ICustomerReviewsMeta>({ data: customerReviewsMeta });
};

export const getCountryNames = async (
  locale: string
): Promise<
  {
    code: string;
    name: string;
  }[]
> => {
  const countryNamesData = flatStrapiCollection<any>({
    data: countryNames,
  }) as ICountry[];
  return countryNamesData.map((x) => ({
    code: x.iso,
    name: getTranslation(x.nameTranslations, locale)?.text || '',
  }));
};

// Aggregated props
export const populateNavBarProps = async (locale: string) => {
  const appLanguages = await getAppLanguages();
  const appCurrencies = await getAppCurrencies();
  const mainNavBar = await getMainNavBar(locale);
  const navBarProps: INavBarProps = {
    items: mainNavBar,
    appPages: [],
    appLanguages,
    appCurrencies,
    appLocale: locale,
    canShow: true,
  };

  return navBarProps;
};

export const populateCookiesPopUpProps = async (locale: string) => {
  const cookiesPopup = await getCookiesPopup(locale);
  const cookiesPopUpProps: ICookiesPopupProps = {
    message: cookiesPopup.message,
    primaryButtonLabel: cookiesPopup.primaryButtonLabel,
    secondaryButtonLabel: cookiesPopup.secondaryButtonLabel,
  };

  return cookiesPopUpProps;
};

export const populateFooterProps = async (locale: string) => {
  const footer = await getMainFooter(locale);
  const allContactMethods = await getContactMethods();
  const footerProps: IFooterProps = {
    items: footer,
    contactMethods: allContactMethods.map((x) => {
      let contactMethod = '';
      const label = getTranslation(x.labelTranslations, locale);
      const value = getTranslation(x.valueTranslations, locale);

      if (label?.text && label?.text !== 'n/a') {
        contactMethod = `${label.text}`;
      }

      if (value?.text && value?.text !== 'n/a') {
        contactMethod += contactMethod ? ` ${value.text}` : value.text;
      }

      return contactMethod;
    }),
  };

  return footerProps;
};

export const scrollToElement = (elementId: string) => {
  const element = document.getElementById(elementId);
  if (!element) {
    return false;
  }

  setTimeout(() => {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });

    setTimeout(() => {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 300);
  }, 150);

  return true;
};

export const scrollToContactUs = () => {
  return scrollToElement('contact');
};

export const isUndefinedOrEmpty = (value: any) => {
  return (
    value === undefined ||
    value === null ||
    (typeof value === 'string' && value.trim() === '') ||
    (Array.isArray(value) && value.length === 0)
  );
};
